import { Sparkles } from "lucide-react";
import { useActiveHappyHours } from "@/features/settings/api";
import { cn } from "@/lib/utils";
import type { Promotion } from "@/types";

function formatDiscount(promo: Promotion) {
  return promo.discountType === "percentage"
    ? `${promo.discountValue}% off`
    : `$${(promo.discountValue / 100).toFixed(2)} off`;
}

interface HappyHourBannerProps {
  variant?: "sidebar" | "inline";
}

export function HappyHourBanner({
  variant = "sidebar",
}: HappyHourBannerProps) {
  const { data } = useActiveHappyHours();
  const active = (Array.isArray(data) ? data : (data?.data ?? [])) as Promotion[];

  if (active.length === 0) {
    return null;
  }

  const isOne = active.length === 1;
  const label = isOne
    ? `${active[0]!.name} — ${formatDiscount(active[0]!)}`
    : `${active.length} happy hours active`;
  const titleAttr = active
    .map((p) => `${p.name} (${formatDiscount(p)})`)
    .join(", ");

  if (variant === "inline") {
    return (
      <div
        role="status"
        aria-live="polite"
        title={titleAttr}
        className="flex items-center gap-1.5 truncate rounded-full bg-amber-100 px-3 py-1 text-xs font-medium text-amber-900 dark:bg-amber-500/15 dark:text-amber-100"
      >
        <Sparkles className="h-3.5 w-3.5 shrink-0" />
        <span className="truncate">{label}</span>
      </div>
    );
  }

  return (
    <div
      role="status"
      aria-live="polite"
      title={titleAttr}
      className={cn(
        "mx-3 mb-2 flex items-center gap-2 rounded-md px-3 py-2 text-xs font-medium",
        "bg-amber-500/15 text-amber-100",
      )}
    >
      <Sparkles className="h-4 w-4 shrink-0" />
      <span className="truncate">{label}</span>
    </div>
  );
}
